/**
 * InitializeChatVideoUseCase
 *
 * Chat counterpart of {@link InitializeVideoUseCase}: initializes encrypted
 * chat-video playback for a chat message `documentId`.
 *
 * Responsibilities (mirrors the user-gen flow exactly — consideration 6):
 * 1. Call the `generateChatMediaCdnTokens(documentId, peerId)` callable (Task 8)
 *    to get the Media CDN HMAC token + the encrypted decryption/iv keys. This
 *    callable authorizes the caller against the two chat participants and the
 *    subscription predicate server-side; a null result is the no-subscription /
 *    permission-denied gate surfacing (the page shows the error / InfoSheet).
 * 2. Run the SAME `videoEncryptionService.processVideoEncryption(documentId, uid,
 *    iat, cdnTokens)` the user-gen path uses (the chat `documentId` is the id) to
 *    decrypt + unshift the keys into a `VideoDecryptionEntity` with
 *    loader-compatible `VideoDecryptionKeys`.
 * 3. Build the chat Media CDN video URL via `MediaUtils.getChatVideoUrl` →
 *    `${CDN_NAME}chat/{user1}/{user2}/{documentId}/master.m3u8?hdnts=<token>`,
 *    using the deterministic sorted user pair (see below).
 * 4. Return `{ success, decryptionEntity, videoUrl, error }` — the SAME result
 *    interface as {@link InitializeVideoUseCase} so the player wiring (Task 15)
 *    can feed `createEncryptedLoader(documentId, decryptionEntity.keys)` to
 *    hls.js unchanged. The loader's `credentials:'include'` CDN transport needs
 *    no change for the chat path.
 *
 * Sorted user pair: the `{user1}/{user2}` URL segment is produced by
 * `MediaUtils.getChatVideoUrl` via `ChatUtils.getStorageGroupChatId` (sorts the
 * two ids lexicographically with `<`), so the playback URL agrees byte-for-byte
 * with the upload path built in `ChatService.uploadChatMedia` (Task 2,
 * `chatService.ts:203-207`) and the `generateChatMediaCdnTokens` CF's
 * `PathGlobs` authorization scope (Task 4). The result is the exact relative
 * path the CF writes to the message's `serverPath` after `transcodeChatVideo`.
 *
 * Web-first design — no Flutter parity (chat media encryption is Web-only).
 */

import type { VideoDecryptionEntity } from '@/domain/entities/video/videoDecryptionEntity';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { videoEncryptionService } from '@/domain/services/video/videoEncryptionService';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { VIDEO_SECRET_KEY_PREFIXES } from '@/domain/constants/videoConstants';

/**
 * Parameters for initializing a chat video for encrypted playback.
 */
export interface ChatVideoInitParams {
  /** Chat message document id — keys the secrets/path; used as the encryption id. */
  documentId: string;

  /** Chat counterparty user id (subscription counterparty for the CF gate). */
  peerId: string;

  /** Current user firebase id (authentication + sorted-pair component). */
  uid: string;

  /** Firebase JWT issued-at timestamp. */
  iat: number;
}

export class InitializeChatVideoUseCase {
  constructor(
    private cloudFunctions = cloudFunctionsService,
    private encryptionService = videoEncryptionService,
  ) { }

  /**
   * Initialize a chat video for encrypted playback.
   *
   * @param params - Chat video initialization parameters.
   * @returns Result with the loader-compatible decryption entity + chat Media
   *   CDN video URL on success; `{ success: false, error }` when the chat
   *   callable returns null (no subscription / permission denied) or throws.
   */
  async execute(params: ChatVideoInitParams): Promise<InitializeChatVideoResult> {
    const { documentId, peerId, uid, iat } = params;

    try {
      // 1. Get chat-media CDN tokens + encrypted keys from the chat callable.
      //    A null result is the no-subscription / permission-denied gate.
      const cdnTokens = await this.cloudFunctions.generateChatMediaCdnTokens(
        documentId,
        peerId,
      );

      if (!cdnTokens) {
        throw new Error('Failed to retrieve chat media CDN tokens from cloud function');
      }

      // 2. Process encryption: decrypt + unshift keys. SAME service the user-gen
      //    path uses; the chat `documentId` is the encryption id (consideration 6).
      //    The CHAT secret-name prefixes MUST be passed so the client unshifts
      //    with the same `expause-chat-*` names the server shifted with — the
      //    user-gen default would produce a garbage key and an AES OperationError.
      const decryptionEntity = await this.encryptionService.processVideoEncryption(
        documentId,
        uid,
        iat,
        cdnTokens,
        VIDEO_SECRET_KEY_PREFIXES.chat,
      );

      // 3. Build the chat Media CDN video URL with the sorted user pair + hdnts.
      const videoUrl = MediaUtils.getChatVideoUrl(uid, peerId, documentId, cdnTokens.hdnts);

      return {
        success: true,
        decryptionEntity,
        videoUrl,
        error: null,
      };
    } catch (error) {
      return {
        success: false,
        decryptionEntity: null,
        videoUrl: null,
        error: error instanceof Error ? error : new Error('Unknown error initializing chat video'),
      };
    }
  }
}

/**
 * Result of chat video initialization. Same shape as
 * {@link InitializeVideoResult} so the player wiring is loader-compatible.
 */
export interface InitializeChatVideoResult {
  /** Whether initialization succeeded. */
  success: boolean;

  /** Decryption entity (null if failed). */
  decryptionEntity: VideoDecryptionEntity | null;

  /** Full chat Media CDN video URL with CDN token (null if failed). */
  videoUrl: string | null;

  /** Error if initialization failed. */
  error: Error | null;
}

// Export factory function for easy instantiation
export const createInitializeChatVideoUseCase = () => new InitializeChatVideoUseCase();
